import { authFetchInit } from './authSession';
import { API_ROOT } from '../config/apiConfig';

export interface ParsedSamlMetadata {
  content: string;
  kind: 'idp' | 'sp' | 'unknown';
  /** SP Entity ID only (undefined for IdP metadata) */
  entityId?: string;
  /** IdP issuer / entityID (always extracted when present) */
  idpEntityId?: string;
  acsUrl?: string;
  ssoLoginUrl?: string;
  nameIdFormat?: string;
  hasCertificate: boolean;
  certificatePem?: string;
}

export interface ParsedOidcDiscovery {
  content: string;
  issuer?: string;
  authorizationEndpoint?: string;
  tokenEndpoint?: string;
  jwksUri?: string;
  discoveryUrl?: string;
}

export interface ExtractedSsoReference {
  kind: 'saml' | 'oidc' | 'mixed' | 'notes';
  metadata_url?: string;
  metadata_content?: string;
  entity_id?: string;
  acs_url?: string;
  has_certificate?: boolean;
  certificate_pem?: string;
  oidc_discovery_endpoint?: string;
  redirect_uris?: string[];
  issuer?: string;
  sso_type_hint?: 'SAML' | 'OIDC';
  summaryLines: string[];
}

function hasMetaTag(xml: string, localName: string): boolean {
  return new RegExp(`<(?:[\\w]+:)?${localName}\\b`, 'i').test(xml);
}

function firstServiceLocation(xml: string, tag: string, preferPost = true): string | undefined {
  const re = new RegExp(`<(?:[\\w]+:)?${tag}\\b([^>]*)/?>`, 'gi');
  const found: Array<{ binding?: string; location: string }> = [];
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml)) !== null) {
    const attrs = m[1] || '';
    const location =
      attrs.match(/Location\s*=\s*"([^"]+)"/i)?.[1]
      || attrs.match(/Location\s*=\s*'([^']+)'/i)?.[1];
    if (!location) continue;
    const binding =
      attrs.match(/Binding\s*=\s*"([^"]+)"/i)?.[1]
      || attrs.match(/Binding\s*=\s*'([^']+)'/i)?.[1];
    found.push({ binding, location: location.replace(/[.,;]+$/, '') });
  }
  if (!found.length) return undefined;
  if (preferPost) {
    const post = found.find(f => /HTTP-POST/i.test(f.binding || ''));
    if (post) return post.location;
  }
  return found[0].location;
}

function metadataFromContent(content: string): ParsedSamlMetadata {
  const text = String(content || '').trim();
  if (!text) {
    throw new Error('Metadata content is empty');
  }
  if (!/EntityDescriptor/i.test(text)) {
    throw new Error('Content does not contain SAML EntityDescriptor metadata');
  }

  const hasIdp = hasMetaTag(text, 'IDPSSODescriptor');
  const hasSp = hasMetaTag(text, 'SPSSODescriptor');
  const hasAcs = hasMetaTag(text, 'AssertionConsumerService');
  const kind: 'idp' | 'sp' | 'unknown' =
    hasSp || hasAcs ? 'sp' : hasIdp ? 'idp' : 'unknown';

  const entityIdRaw =
    text.match(/\bentityID\s*=\s*"([^"]+)"/i)?.[1]
    || text.match(/\bentityID\s*=\s*'([^']+)'/i)?.[1];

  const acsUrl =
    kind === 'sp'
      ? firstServiceLocation(text, 'AssertionConsumerService', true)
      : undefined;

  const ssoLoginUrl =
    kind === 'idp' || kind === 'unknown'
      ? firstServiceLocation(text, 'SingleSignOnService', true)
      : undefined;

  const nameIdFormat =
    text.match(/<(?:[\w]+:)?NameIDFormat[^>]*>\s*([^<]+)\s*<\/(?:[\w]+:)?NameIDFormat>/i)?.[1]?.trim();

  const certRaw =
    text.match(/<(?:[\w]+:)?X509Certificate[^>]*>\s*([\s\S]+?)\s*<\/(?:[\w]+:)?X509Certificate>/i)?.[1];
  const certClean = certRaw ? certRaw.replace(/\s+/g, '') : undefined;

  return {
    content: text,
    kind,
    // IdP entityID must not become the app SP Entity ID
    entityId: kind === 'sp' ? entityIdRaw : undefined,
    idpEntityId: kind === 'idp' ? entityIdRaw : undefined,
    acsUrl,
    ssoLoginUrl,
    nameIdFormat,
    hasCertificate: /X509Certificate/i.test(text),
    certificatePem: certClean
      ? `-----BEGIN CERTIFICATE-----\n${certClean}\n-----END CERTIFICATE-----`
      : undefined,
  };
}

export function detectIdpPlatformFromMetadataUrl(url?: string): 'okta' | 'azure' | undefined {
  const u = String(url || '').toLowerCase();
  if (!u) return undefined;
  if (/\.okta\.com\b|\.oktapreview\.com\b|\.okta-emea\.com\b/i.test(u)) return 'okta';
  if (/login\.microsoftonline\.com|sts\.windows\.net|microsoftonline/i.test(u)) return 'azure';
  return undefined;
}

export function extractOktaAppIdFromMetadata(urlOrEntityId?: string): string | undefined {
  const s = String(urlOrEntityId || '');
  return s.match(/\/app\/(exk[a-zA-Z0-9]+)\b/i)?.[1]
    || s.match(/\b(exk[a-zA-Z0-9]+)\b/i)?.[1];
}

export function parseSamlMetadataContent(content: string): ParsedSamlMetadata {
  return metadataFromContent(content);
}

export function isMetadataXmlContent(value: string): boolean {
  const v = value.trim();
  return v.startsWith('<?xml') || /<(\w+:)?EntityDescriptor\b/i.test(v);
}

export function isOidcDiscoveryJson(value: string): boolean {
  const v = value.trim();
  if (!v.startsWith('{')) return false;
  try {
    const json = JSON.parse(v);
    return !!(
      json
      && typeof json === 'object'
      && (json.issuer || json.authorization_endpoint || json.jwks_uri)
    );
  } catch {
    return false;
  }
}

export function parseOidcDiscoveryContent(content: string, sourceUrl?: string): ParsedOidcDiscovery {
  const text = String(content || '').trim();
  if (!text) throw new Error('OIDC discovery content is empty');
  let json: Record<string, unknown>;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error('Content is not valid OIDC discovery JSON');
  }
  if (!json.issuer && !json.authorization_endpoint) {
    throw new Error('JSON is missing issuer / authorization_endpoint');
  }
  return {
    content: text,
    issuer: typeof json.issuer === 'string' ? json.issuer : undefined,
    authorizationEndpoint:
      typeof json.authorization_endpoint === 'string' ? json.authorization_endpoint : undefined,
    tokenEndpoint: typeof json.token_endpoint === 'string' ? json.token_endpoint : undefined,
    jwksUri: typeof json.jwks_uri === 'string' ? json.jwks_uri : undefined,
    discoveryUrl: sourceUrl,
  };
}

export function isOidcDiscoveryUrl(value: string): boolean {
  const v = value.trim();
  return (
    /^https:\/\//i.test(v)
    && (/openid-configuration/i.test(v) || /\.well-known\//i.test(v) || /oauth2?\/v2?\.0/i.test(v))
  );
}

export function isMetadataUrl(value: string): boolean {
  const v = value.trim();
  return (
    /^https?:\/\//i.test(v)
    && (
      /\.xml(\?|$)/i.test(v)
      || /metadata/i.test(v)
      || /federationmetadata/i.test(v)
      || /saml/i.test(v)
      || isOidcDiscoveryUrl(v)
    )
  );
}

function uniqueHttpsUrls(text: string): string[] {
  const matches = text.match(/https:\/\/[^\s"'<>\]\)]+/gi) || [];
  return Array.from(new Set(matches.map(u => u.replace(/[.,;]+$/, ''))));
}

/** Pull SAML/OIDC fields from free-text vendor docs, guides, or pasted notes. */
export function extractSsoDetailsFromReferenceText(text: string): ExtractedSsoReference {
  const raw = String(text || '').trim();
  const summaryLines: string[] = [];
  const result: ExtractedSsoReference = { kind: 'notes', summaryLines };

  if (!raw) return result;

  if (isMetadataXmlContent(raw)) {
    try {
      const saml = parseSamlMetadataContent(raw);
      result.kind = 'saml';
      result.sso_type_hint = 'SAML';
      result.metadata_content = saml.content.slice(0, 8000);
      result.entity_id = saml.entityId;
      result.acs_url = saml.acsUrl;
      result.has_certificate = saml.hasCertificate;
      result.certificate_pem = saml.certificatePem;
      if (saml.entityId) summaryLines.push(`• **Entity ID:** ${saml.entityId}`);
      if (saml.acsUrl) summaryLines.push(`• **ACS URL:** ${saml.acsUrl}`);
      summaryLines.push(`• **Certificate:** ${saml.hasCertificate ? 'Found ✓' : 'Not found'}`);
      return result;
    } catch {
      /* fall through */
    }
  }

  if (isOidcDiscoveryJson(raw)) {
    try {
      const oidc = parseOidcDiscoveryContent(raw);
      result.kind = 'oidc';
      result.sso_type_hint = 'OIDC';
      result.metadata_content = oidc.content.slice(0, 8000);
      result.issuer = oidc.issuer;
      result.oidc_discovery_endpoint = oidc.discoveryUrl || oidc.issuer;
      if (oidc.issuer) summaryLines.push(`• **Issuer:** ${oidc.issuer}`);
      if (oidc.authorizationEndpoint) summaryLines.push(`• **Authorize:** ${oidc.authorizationEndpoint}`);
      return result;
    } catch {
      /* fall through */
    }
  }

  const entityId =
    raw.match(/\bentity[_ ]?id\s*[:=]\s*["']?(\S+)/i)?.[1]
    || raw.match(/\bentityID\s*=\s*"([^"]+)"/i)?.[1];
  const acsUrl =
    raw.match(/\bacs[_ ]?url\s*[:=]\s*["']?(https:\/\/\S+)/i)?.[1]
    || raw.match(/AssertionConsumerService[^>]*Location="([^"]+)"/i)?.[1];
  const discovery =
    raw.match(/\b(?:oidc[_ ]?discovery|discovery[_ ]?(?:url|endpoint)|openid[_-]?configuration)\s*[:=]\s*["']?(https:\/\/\S+)/i)?.[1]
    || uniqueHttpsUrls(raw).find(u => isOidcDiscoveryUrl(u));
  const redirectMatches = [
    ...(raw.match(/\bredirect[_ ]?uri(?:s)?\s*[:=]\s*["']?(https:\/\/[^\s,"']+)/gi) || []),
  ]
    .map(line => line.match(/https:\/\/\S+/i)?.[0]?.replace(/[.,;]+$/, ''))
    .filter((u): u is string => !!u);
  const redirectsFromList = uniqueHttpsUrls(raw).filter(u =>
    /callback|redirect|oauth|oidc|signin-oidc|auth\/callback/i.test(u),
  );
  const redirect_uris = Array.from(new Set([...redirectMatches, ...redirectsFromList])).slice(0, 8);

  const samlMetaUrl = uniqueHttpsUrls(raw).find(u => isMetadataUrl(u) && !isOidcDiscoveryUrl(u));

  if (entityId) {
    result.entity_id = entityId.replace(/[.,;]+$/, '');
    summaryLines.push(`• **Entity ID:** ${result.entity_id}`);
  }
  if (acsUrl) {
    result.acs_url = acsUrl.replace(/[.,;]+$/, '');
    summaryLines.push(`• **ACS URL:** ${result.acs_url}`);
  }
  if (discovery) {
    result.oidc_discovery_endpoint = discovery.replace(/[.,;]+$/, '');
    summaryLines.push(`• **OIDC discovery:** ${result.oidc_discovery_endpoint}`);
  }
  if (samlMetaUrl) {
    result.metadata_url = samlMetaUrl;
    summaryLines.push(`• **Metadata URL:** ${samlMetaUrl}`);
  }
  if (redirect_uris.length) {
    result.redirect_uris = redirect_uris;
    summaryLines.push(`• **Redirect URI(s):** ${redirect_uris.join(', ')}`);
  }

  const hasSaml = !!(result.entity_id || result.acs_url || (result.metadata_url && !discovery));
  const hasOidc = !!(result.oidc_discovery_endpoint || redirect_uris.length);
  if (hasSaml && hasOidc) {
    result.kind = 'mixed';
  } else if (hasSaml) {
    result.kind = 'saml';
    result.sso_type_hint = 'SAML';
  } else if (hasOidc) {
    result.kind = 'oidc';
    result.sso_type_hint = 'OIDC';
  }

  return result;
}

async function fetchMetadataUrlRaw(url: string): Promise<{ content: string; kind?: string }> {
  const res = await fetch(`${API_ROOT}/sso/fetch-metadata-url`, authFetchInit({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ metadata_url: url.trim() }),
  }));
  const json = await res.json();
  if (!res.ok || !json.success) {
    throw new Error(json.error || 'Failed to fetch metadata URL');
  }
  return {
    content: String(json.data?.content || ''),
    kind: typeof json.data?.kind === 'string' ? json.data.kind : undefined,
  };
}

/** Fetch SAML metadata via backend proxy (follows redirects, avoids CORS). */
export async function fetchSamlMetadataFromUrl(url: string): Promise<ParsedSamlMetadata> {
  const { content } = await fetchMetadataUrlRaw(url);
  return metadataFromContent(content);
}

/** Fetch SAML or OIDC discovery via SSO proxy. */
export async function fetchSsoMetadataFromUrl(url: string): Promise<ExtractedSsoReference> {
  const { content, kind } = await fetchMetadataUrlRaw(url);
  if (kind === 'oidc' || isOidcDiscoveryJson(content)) {
    const oidc = parseOidcDiscoveryContent(content, url.trim());
    return {
      kind: 'oidc',
      sso_type_hint: 'OIDC',
      oidc_discovery_endpoint: url.trim(),
      issuer: oidc.issuer,
      metadata_content: oidc.content.slice(0, 8000),
      metadata_url: url.trim(),
      summaryLines: [
        `I fetched your **OIDC discovery** document:`,
        oidc.issuer ? `• **Issuer:** ${oidc.issuer}` : null,
        oidc.authorizationEndpoint ? `• **Authorize:** ${oidc.authorizationEndpoint}` : null,
        oidc.tokenEndpoint ? `• **Token:** ${oidc.tokenEndpoint}` : null,
      ].filter(Boolean) as string[],
    };
  }
  const saml = metadataFromContent(content);
  const isIdp = saml.kind === 'idp';
  return {
    kind: 'saml',
    sso_type_hint: 'SAML',
    metadata_url: url.trim(),
    metadata_content: saml.content.slice(0, 512_000),
    entity_id: saml.entityId,
    acs_url: saml.acsUrl,
    has_certificate: saml.hasCertificate,
    certificate_pem: saml.certificatePem,
    summaryLines: isIdp
      ? [
          'I fetched IdP federation metadata (not app/SP metadata):',
          saml.idpEntityId ? `• IdP Entity ID / Issuer: ${saml.idpEntityId}` : null,
          saml.ssoLoginUrl ? `• IdP SSO URL: ${saml.ssoLoginUrl}` : null,
          `• Metadata URL: ${url.trim()}`,
          `• Certificate: ${saml.hasCertificate ? 'Found' : 'Not found'}`,
          'ACS URL is not in this file. Metadata URL is saved so you can submit; paste the app Reply URL / ACS when you have it.',
        ].filter(Boolean) as string[]
      : ([
          'I fetched and parsed your SAML metadata from URL:',
          saml.entityId ? `• Entity ID: ${saml.entityId}` : null,
          saml.acsUrl ? `• ACS URL: ${saml.acsUrl}` : null,
          `• Certificate: ${saml.hasCertificate ? 'Found' : 'Not found'}`,
        ].filter(Boolean) as string[]),
  };
}

/** Try SSO metadata proxy first for XML/OIDC URLs, then assistant fetch-url for docs/pages. */
export async function fetchUrlContentForVysor(url: string): Promise<{ text: string; source: 'assistant' | 'sso-metadata' }> {
  const trimmed = url.trim();

  if (isMetadataUrl(trimmed)) {
    try {
      const { content } = await fetchMetadataUrlRaw(trimmed);
      // Keep enough for Microsoft FederationMetadata (certs before SSO endpoints)
      return { text: content.slice(0, 512_000), source: 'sso-metadata' };
    } catch {
      // Fall through to assistant fetch for docs / non-strict metadata hosts
    }
  }

  const assistantRes = await fetch(`${API_ROOT}/assistant/fetch-url`, authFetchInit({
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url: trimmed }),
  }));
  if (assistantRes.ok) {
    const result = await assistantRes.json();
    if (result.success && result.text) {
      return { text: result.text, source: 'assistant' };
    }
  }

  const errJson = await assistantRes.json().catch(() => ({}));
  throw new Error(errJson.error || 'Could not fetch URL content');
}

/**
 * Resolve a pasted URL, XML, discovery JSON, or vendor guide text into SSO fields.
 * Never throws for "not SAML" — returns notes + whatever could be extracted.
 */
export async function resolveSsoReferenceInput(
  rawValue: string,
): Promise<{ extracted: ExtractedSsoReference; error?: string }> {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return { extracted: { kind: 'notes', summaryLines: [] } };
  }

  if (/^http:\/\//i.test(trimmed)) {
    return {
      extracted: { kind: 'notes', summaryLines: [] },
      error: 'Only HTTPS URLs are allowed. Please use an `https://` URL, paste XML/JSON, or choose **Skip**.',
    };
  }

  // Direct paste: XML / OIDC JSON / free text with fields
  if (!/^https:\/\//i.test(trimmed)) {
    return { extracted: extractSsoDetailsFromReferenceText(trimmed) };
  }

  // HTTPS URL — try SSO proxy (SAML + OIDC), then assistant page fetch + extract
  try {
    const fromProxy = await fetchSsoMetadataFromUrl(trimmed);
    return { extracted: fromProxy };
  } catch (proxyErr) {
    try {
      const fetched = await fetchUrlContentForVysor(trimmed);
      const extracted = extractSsoDetailsFromReferenceText(fetched.text);
      if (!extracted.metadata_url && isMetadataUrl(trimmed)) {
        extracted.metadata_url = trimmed;
      }
      if (!extracted.oidc_discovery_endpoint && isOidcDiscoveryUrl(trimmed)) {
        extracted.oidc_discovery_endpoint = trimmed;
        extracted.sso_type_hint = extracted.sso_type_hint || 'OIDC';
        extracted.kind = extracted.kind === 'notes' ? 'oidc' : extracted.kind;
      }
      if (!extracted.summaryLines.length) {
        extracted.summaryLines = [
          `I fetched your reference URL and will keep it as notes for SSO review.`,
          `• **URL:** ${trimmed}`,
        ];
      } else {
        extracted.summaryLines = [
          `I fetched your reference and captured these details:`,
          ...extracted.summaryLines,
        ];
      }
      // Keep original URL as notes when we only got partial extract
      return { extracted: { ...extracted, metadata_url: extracted.metadata_url || trimmed } };
    } catch {
      const msg = proxyErr instanceof Error ? proxyErr.message : 'Could not fetch URL';
      return {
        extracted: {
          kind: 'notes',
          metadata_url: trimmed,
          summaryLines: [`I could not fetch the URL automatically — I'll save it as a note.`],
        },
        error: `${msg}. You can paste the XML/JSON directly, attach a guide, or choose **Skip**.`,
      };
    }
  }
}

/** Apply extracted reference fields onto wizard collected values. */
export function applyExtractedSsoToCollected(
  collected: Record<string, string>,
  extracted: ExtractedSsoReference,
  rawValue: string,
): Record<string, string> {
  const next: Record<string, string> = { ...collected, metadata_or_notes: rawValue.trim() };
  if (extracted.metadata_url) next.metadata_url = extracted.metadata_url;
  if (extracted.metadata_content) next.metadata_content = extracted.metadata_content;
  if (extracted.entity_id) next.metadata_entity_id = extracted.entity_id;
  if (extracted.acs_url) next.metadata_acs_url = extracted.acs_url;
  if (extracted.has_certificate != null) next.metadata_has_certificate = String(extracted.has_certificate);
  if (extracted.certificate_pem) next.metadata_certificate = extracted.certificate_pem;
  if (extracted.oidc_discovery_endpoint) next.oidc_discovery_endpoint = extracted.oidc_discovery_endpoint;
  if (extracted.issuer) next.oidc_issuer = extracted.issuer;
  if (extracted.redirect_uris?.length) next.redirect_uris = extracted.redirect_uris.join('\n');
  if (extracted.sso_type_hint && !collected.sso_type) {
    next.sso_type = extracted.sso_type_hint;
  }
  return next;
}
